/**
 * Phase 6 + 7 — Tenant-aware cache namespacing and reset.
 *
 * Use these helpers to scope React Query keys and any in-memory caches
 * by the active tenant_id. Subscribers wired here also clear
 * tenant-scoped localStorage entries on tenant switch so that a freshly
 * scoped Store Manager session never inherits cached main-site data
 * (catalog, media, theme, cart, etc.).
 */
import { useTenantStore } from "./tenantContext";

/** Stable cache key prefix for the active tenant scope. */
export function tenantKey(scope: string, parts: ReadonlyArray<string | number | null | undefined> = []): readonly unknown[] {
  const tenantId = useTenantStore.getState().tenantId ?? "__main__";
  return ["tenant", tenantId, scope, ...parts] as const;
}

/** React hook form — re-renders when tenant changes. */
export function useTenantQueryKey(scope: string, parts: ReadonlyArray<string | number | null | undefined> = []): readonly unknown[] {
  const tenantId = useTenantStore(s => s.tenantId) ?? "__main__";
  return ["tenant", tenantId, scope, ...parts] as const;
}

/**
 * localStorage keys that mirror tenant-scoped Zustand stores. On tenant
 * switch these are wiped so a Store Manager admin/storefront NEVER inherits
 * main-site (or another tenant's) cached catalog, media, theme, cart, etc.
 *
 * IMPORTANT: This list is only flushed during a tenant-scope CHANGE — when
 * the active scope transitions from one id (or null=main) to another. It is
 * never run automatically on the main site at boot, so existing visitors to
 * the main storefront are unaffected.
 */
const TENANT_SCOPED_LS_KEYS: ReadonlyArray<string> = [
  // Cart / session / favorites
  "khays_cart",
  "khays_favorites",
  "khays_recent_devices",
  "khays_storefront_session",
  // Admin / storefront catalog
  "khays_admin_settings",
  "khays_custom_devices",
  "khays_custom_categories",
  "khays_custom_brands",
  "khays_category_order",
  "khays_category_renames",
  "khays_brand_renames",
  "khays_device_renames",
  "khays_device_settings",
  "khays_category_settings",
  "khays_removed_devices",
  "khays_deleted_builtin_brands",
  "khays_deleted_builtin_categories",
  "khays_deleted_builtin_devices",
  "khays_extra_variants",
  "khays_variant_overrides",
  "khays_custom_sub_variants",
  "khays_sub_variant_category_settings",
  "khays_sub_variant_device_settings",
  "khays_device_subcondition_modes",
  // Inventory / stock
  "khays_stock",
  "khays_shipping_options",
  "khays_shipping_category_assignments",
  // Orders / enquiries / customers (tenant-scoped commerce data)
  "khays_orders",
  "khays_enquiries",
  "khays_message_templates",
  "khays_deletion_audit",
  "khays_session_logs",
  "khays_activity_log",
  "khays_staff_notification_settings",
  // Media / branding
  "khays_media",
  "khays_media_recycled",
  "khays_storefront_settings",
  "khays_storefront_theme",
  "khays_watermark_settings",
  "khays_about",
  "khays_reviews",
  "khays_faqs",
  "khays_ad_slots",
  "khays_tutorial_video",
  "khays_saved_colors",
  "khays_color_templates",
  // Pricing / flash sale / featured
  "khays_flash_sale",
  "khays_featured_config",
  "khays_featured_cache",
  "khays_auto_pricing",
  // Swap
  "khays_swap_settings",
  "khays_swap_pricing",
  "khays_swap_tradein_pricing",
  "khays_swap_condition_templates",
  "khays_swap_notes",
  "khays_condition_guide",
  // Tracking
  "khays_tracking_presets",
  "khays_tracking_settings",
  // Modern / Lite themes
  "khays_modern_menus",
  "khays_modern_tabs",
  "khays_lite_tabs",
  "khays_lite_menu_visibility",
  // Misc
  "khays_device_links",
  "khays_menu_visibility",
  "khays_settings_items_visibility",
  // Per-user notification baselines (cleared so a store_manager doesn't carry
  // a main-site baseline that suppresses tenant-scoped new-order alerts).
  "khays_last_notif_check",
];


/**
 * localStorage key prefixes that fan out to per-user / per-tenant variants
 * (e.g. `khays_last_notif_check:<userId>`, `khays_lite_cart__t_<tenantId>`).
 * On scope change, every key starting with one of these prefixes is wiped.
 */
const TENANT_SCOPED_LS_PREFIXES: ReadonlyArray<string> = [
  "khays_last_notif_check",
  "khays_lite_cart",
  "khays_lite_favorites",
  "khays_lite_user_",
];

/** Wipe all tenant-scoped client state. Safe to call multiple times. */
export function resetTenantScopedStores(): void {
  try {
    for (const k of TENANT_SCOPED_LS_KEYS) {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    }
    try {
      const toDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (TENANT_SCOPED_LS_PREFIXES.some(p => k === p || k.startsWith(p + ":") || k.startsWith(p + "__"))) {
          toDelete.push(k);
        }
      }
      for (const k of toDelete) {
        try { localStorage.removeItem(k); } catch { /* noop */ }
      }
    } catch { /* noop */ }
    try {
      window.dispatchEvent(new CustomEvent("khays:tenant-scope-changed"));
    } catch { /* noop */ }
  } catch { /* noop */ }
}


/**
 * Mount once at app boot: subscribes to tenant changes and wipes
 * tenant-scoped client state ON CHANGE (never on the initial null→null
 * transition for the main site). Idempotent.
 */
let _subscribed = false;
export function installTenantScopeSubscriber(): void {
  if (_subscribed) return;
  _subscribed = true;
  let lastId: string | null = useTenantStore.getState().tenantId;
  useTenantStore.subscribe((state) => {
    if (state.tenantId !== lastId) {
      lastId = state.tenantId;
      resetTenantScopedStores();
    }
  });
}
